import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import CommonPage from '../components/common-page/CommonPage';
import CommonTabPanel from '../components/common-tabs/CommonTabPanel';
import CommonTabs from '../components/common-tabs/CommonTabs';
import { ID8_GEN_TYPES, ID8_TABS, PROMPT_TYPE } from '../constants';
import useBindAction from '../hooks/useBindAction';
import { clearProjectStatus } from '../redux/global/globalActions';
import { generateMessages, getId8FormData, replyId8, resetSession, saveMessageChanges } from '../redux/id8/id8Actions';
import { changeActiveGenStatus, changeId8Tab, resetId8Params } from '../redux/id8/id8Reducer';
import { uploadFile } from '../redux/scout/scoutActions';
import { clearId8ImageCaches } from '../utils/clearId8ImageCaches';
import BackupTab from './components/backup/BackupTab';
import FrameTab from './components/frames/FrameTab';
import GenerationSection from './components/generation-section/GenerationSection';
import IdeasTab from './components/ideas/IdeasTab';
import webSocketManager from '../service/WebSocketManager';
import UnsavedModalController from './components/unsaved-modal-controller/UnsavedModalController';
import LikedTab from './components/liked/LikedTab';
import RecreateTab from './components/recreate/RecreateTab';
import useId8Tabs from './hooks/useId8Tabs';
import callApi from '../service/callApi';

import scss from './Id8.module.scss';

function Id8() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const id8Tab = useSelector((state) => state.id8.id8Tab);
  const isDirty = useSelector((state) => state.id8.isDirty);

  const [isShowGenerationForm, setIsShowGenerationForm] = useState(true);
  const [isShowUnsavedModal, setIsShowUnsavedModal] = useState(false);

  const { triggerAction, bindAction, resetAction } = useBindAction(isDirty, () => handleShowModal());

  const TABS = useId8Tabs();

  const initialImagesGenerationStatusCheck = async () => {
    const response = await callApi.get(
      `/api/project/status/is/id8/content/generating?id=${id}&contentType=${PROMPT_TYPE.IDEA}`,
    );

    dispatch(changeActiveGenStatus({ genType: ID8_GEN_TYPES.IMAGES, status: response.data }));
  };

  const handleShowModal = () => {
    setIsShowUnsavedModal(true);
  };

  const handleChangeTabs = (_, newTab) => {
    bindAction(() => dispatch(changeId8Tab(newTab)));
  };

  const handleSaveModal = async () => {
    await dispatch(saveMessageChanges());
  };

  const handleResetSession = () => {
    dispatch(resetId8Params());
    dispatch(clearProjectStatus({ id }));
    clearId8ImageCaches();
    webSocketManager.closeProjectConnection(id);
  };

  useEffect(() => {
    if (id) {
      dispatch(getId8FormData({ id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    initialImagesGenerationStatusCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      handleResetSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', handleResetSession);

    return () => {
      window.removeEventListener('beforeunload', handleResetSession);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CommonPage className={scss.pageWrapper}>
      <GenerationSection
        isShowGenerationForm={isShowGenerationForm}
        onShowGenerationForm={setIsShowGenerationForm}
        onReply={replyId8}
        onGenerateMessages={generateMessages}
        onResetSession={resetSession}
        onFileUpload={uploadFile}
      />

      <CommonTabs tabs={TABS} value={id8Tab} onChange={handleChangeTabs} isFullWidth />

      <CommonTabPanel value={id8Tab} index={ID8_TABS.IDEAS}>
        <IdeasTab bindAction={bindAction} isShowGenerationForm={isShowGenerationForm} />
      </CommonTabPanel>
      <CommonTabPanel value={id8Tab} index={ID8_TABS.IMAGES}>
        <FrameTab />
      </CommonTabPanel>
      <CommonTabPanel value={id8Tab} index={ID8_TABS.RECREATE}>
        <RecreateTab />
      </CommonTabPanel>
      <CommonTabPanel value={id8Tab} index={ID8_TABS.LIKED}>
        <LikedTab />
      </CommonTabPanel>
      <CommonTabPanel value={id8Tab} index={ID8_TABS.BACKUP}>
        <BackupTab bindAction={bindAction} isShowGenerationForm={isShowGenerationForm} />
      </CommonTabPanel>

      <UnsavedModalController
        isShowUnsavedModal={isShowUnsavedModal}
        onChangeUnsavedModalVisibility={setIsShowUnsavedModal}
        resetAction={resetAction}
        triggerAction={triggerAction}
        onSaveChanges={handleSaveModal}
      />
    </CommonPage>
  );
}

export default Id8;
